// Auto-detects new deploys and forces a hard reload so users (especially
// inside the Android TWA) never get stuck on a stale JS bundle.
//
// Strategy:
//   1. On app load, fetch `/api/version`. Store it in localStorage.
//   2. Every 60s while the app is open, re-fetch `/api/version`. If the value
//      differs from the cached one, the backend has been redeployed → call
//      `window.location.reload()` (the new index.html arrives with a new JS
//      bundle hash that bypasses the immutable cache).
//   3. Also re-check whenever the tab regains focus (the user has been in the
//      background for an unknown amount of time).

import { useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'app_version';
const POLL_INTERVAL_MS = 60_000;

const fetchVersion = async () => {
  try {
    const res = await axios.get(`${API}/version`, { timeout: 5_000 });
    return res.data?.version || null;
  } catch {
    return null;
  }
};

const reloadIfChanged = async () => {
  const remote = await fetchVersion();
  if (!remote) return;
  const local = localStorage.getItem(STORAGE_KEY);
  if (!local) {
    // First boot — just remember the version.
    localStorage.setItem(STORAGE_KEY, remote);
    return;
  }
  if (local !== remote) {
    // New deploy detected — update the marker BEFORE reloading so we don't
    // loop forever if the new bundle still reads a different local value
    // before its own first run completes.
    localStorage.setItem(STORAGE_KEY, remote);
    window.location.reload();
  }
};

export function useVersionCheck() {
  useEffect(() => {
    reloadIfChanged();
    const interval = setInterval(reloadIfChanged, POLL_INTERVAL_MS);
    const onFocus = () => reloadIfChanged();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reloadIfChanged();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
